import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import { NavigationAccessBoundary } from "./contexts/NavigationAccessContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Register from "./pages/Register";
import Login from "./pages/Login";
import SubscriptionPurchase from "./pages/SubscriptionPurchase";
import Dashboard from "./components/Dashboard";
import DashboardHome from "./pages/DashboardHome";
import AppsHub from "./pages/AppsHub";
import AppDetailPage from "./pages/AppDetailPage";
import Company from "./pages/Company";
import Departments from "./pages/master/Departments";
import Lines from "./pages/master/Lines";
import Products from "./pages/master/Products";
import ProductSkus from "./pages/master/ProductSkus";
import Shifts from "./pages/master/Shifts";
import LossTypes from "./pages/master/LossTypes";
import Roles from "./pages/master/Roles";
import PermissionsPage from "./pages/master/PermissionsPage";
import Categories from "./pages/master/Categories";
import Statuses from "./pages/master/Statuses";
import LossSubTypes from "./pages/master/LossSubTypes";
import LossReasons from "./pages/master/LossReasons";
import Triggers from "./pages/master/Triggers";
import CycleTimes from "./pages/master/CycleTimes";
import PlannedDowntimes from "./pages/master/PlannedDowntimes";
import OEEThresholds from "./pages/master/OEEThresholds";
import AttachmentTypes from "./pages/master/AttachmentTypes";
import ImpactLevels from "./pages/master/ImpactLevels";

import RootCauseCategories from "./pages/master/RootCauseCategories";
import FailureModes from "./pages/master/FailureModes";
import PPEList from "./pages/master/PPEList";
import PlannedMaintenanceMasters from "./pages/master/PlannedMaintenanceMasters";
import ContractorMasters from "./pages/master/ContractorMasters";
import MaintenancePlansPage from "./pages/master/pm/MaintenancePlansPage";
import MaintenancePlanTasksPage from "./pages/master/pm/MaintenancePlanTasksPage";
import PlannedMaintenanceDashboard from "./pages/master/pm/PlannedMaintenanceDashboard";
import PmMatrixPlannerPage from "./pages/master/pm/matrix/PmMatrixPlannerPage";
import PmSchedulesPage from "./pages/master/pm/PmSchedulesPage";
import MaintenanceWindowsPage from "./pages/master/pm/MaintenanceWindowsPage";
import TechnicianSkillsPage from "./pages/master/pm/TechnicianSkillsPage";
import TechnicianTeamsPage from "./pages/master/pm/TechnicianTeamsPage";
import ToolsPage from "./pages/master/pm/ToolsPage";
import PpeSafetyPage from "./pages/master/pm/PpeSafetyPage";
import SensorTagsPage from "./pages/master/pm/SensorTagsPage";
import SensorThresholdsPage from "./pages/master/pm/SensorThresholdsPage";
import PmTriggerLogsPage from "./pages/master/pm/PmTriggerLogsPage";
import MeterReadingsPage from "./pages/master/pm/MeterReadingsPage";
import PmHistoryPage from "./pages/master/pm/PmHistoryPage";
import PmCompliancePage from "./pages/master/pm/PmCompliancePage";

import WorkOrderPrioritiesPage from "./pages/master/pm/WorkOrderPrioritiesPage";
import LockoutTagoutPage from "./pages/master/pm/LockoutTagoutPage";
import SopDocumentsPage from "./pages/master/pm/SopDocumentsPage";
import HolidayCalendarPage from "./pages/master/pm/HolidayCalendarPage";
import VendorsPage from "./pages/master/pm/VendorsPage";
import MaintenanceSlaPage from "./pages/master/pm/MaintenanceSlaPage";
import NotificationTemplatesPage from "./pages/master/pm/NotificationTemplatesPage";
import Plants from "./pages/Plants";
import Areas from "./pages/Areas";

import Kaizen from "./pages/Kaizen";
import KaizenEntry from "./pages/KaizenEntry";
import KaizenActions from "./pages/KaizenActions";
import RCA from "./pages/RCA";
import RCAEntry from "./pages/RCAEntry";
import RCAActions from "./pages/RCAActions";
import FishboneAnalysis from "./pages/FishboneAnalysis";
import FiveWhysAnalysis from "./pages/FiveWhysAnalysis";
import CAPA from "./pages/CAPA";
import CAPAEntry from "./pages/CAPAEntry";
import CAPATaskManagement from "./pages/CAPATaskManagement";
import OPLDocuments from "./pages/OPLDocuments";
import Users from "./pages/Users";
import Subscription from "./pages/Subscription";
import ChangePassword from "./pages/ChangePassword";
import MasterDataImport from "./pages/MasterDataImport";
import DowntimeReport from "./pages/DowntimeReport";
import PmScheduleComplianceReport from "./pages/PmScheduleComplianceReport";
import PmOverdueReport from "./pages/PmOverdueReport";
import IndividualPmTaskReport from "./pages/IndividualPmTaskReport";
import PmStandardsTracker from "./pages/PmStandardsTracker";
import MTBRReport from "./pages/MTBRReport";
import MTBFReport from "./pages/MTBFReport";
import BreakdownLog from "./pages/BreakdownLog";
import WorkOrder from "./pages/WorkOrder";
import WorkOrderWizard from "./pages/WorkOrderWizard";
import WorkPermitList from "./pages/WorkPermitList";
import WorkPermitWizard from "./pages/WorkPermitWizard";
import WorkPermitDashboard from "./pages/WorkPermitDashboard";
import WorkPermitExpiringReport from "./pages/WorkPermitExpiringReport";
import WorkPermitStatusReport from "./pages/WorkPermitStatusReport";
import WorkPermitComplianceReport from "./pages/WorkPermitComplianceReport";
import SpareParts from "./pages/SpareParts";
import InventoryDashboard from "./pages/InventoryDashboard";
import InventoryHistory from "./pages/InventoryHistory";
import SparePartsReports from "./pages/SparePartsReports";
import SafetyObservation from "./pages/SafetyObservation";
import SafetyObservationEntry from "./pages/SafetyObservationEntry";
import SafetyObservationActions from "./pages/SafetyObservationActions";
import SafetyObservationDashboard from "./pages/SafetyObservationDashboard";
import SafetyObservationReports from "./pages/SafetyObservationReports";
import IncidentDashboard from "./pages/IncidentModule/IncidentDashboard";
import IncidentAnalyticsDashboard from "./pages/IncidentModule/IncidentAnalyticsDashboard";
import IncidentStepper from "./pages/IncidentModule/IncidentStepper";
import HazardReportList from "./pages/HazardReportList";
import HazardReportForm from "./pages/HazardReportForm";
import HazardReportDashboard from "./pages/HazardReportDashboard";
import HazardSettings from "./pages/HazardSettings";
import SafetyBehaviorCategories from "./pages/master/SafetyBehaviorCategories";
import HazardCategories from "./pages/master/HazardCategories";
import Schedule from "./pages/SafetyBehaviorObservation/schedule/Schedule";
import SBOSettings from "./pages/SafetyBehaviorObservation/schedule/SBOSettings";
import ObservationType from "./pages/SafetyBehaviorObservation/observationtype/ObservationType";
import SiteLayout from "./pages/SafetyBehaviorObservation/sitelayout/SiteLayout";
import BiDashboard from "./pages/BiDashboard";
import MetricCardLibrary from "./pages/MetricCardLibrary";
import OeeContainer from "./pages/OeeModule/OeeContainer";
import OeeSettings from "./pages/OeeModule/OeeSettings";

import OeeLossReport from "./pages/OeeModule/OeeLossReport";
import OeeFinancialLossReport from "./pages/OeeModule/OeeFinancialLossReport";
import OeeLossTrendsReport from "./pages/OeeModule/OeeLossTrendsReport";
import OeeSummaryReport from "./pages/OeeModule/OeeSummaryReport";
import PMStandardsPage from "./pages/master/pm/PMStandardsPage";
import OperatorPmTasks from "./pages/master/pm/OperatorPmTasks";
import PmCalendar from "./pages/master/pm/PmCalendar";
import AssetMaster from "./pages/AssetMaster";
import FiveS from "./pages/FiveS";
import ReportsDashboard from "./pages/ReportsDashboard";
import HazardReports from "./pages/HazardReports";
import FiveSEntry from "./pages/FiveSEntry";
import UserProfile from "./pages/UserProfile";
import ActivityLog from "./pages/ActivityLog";
import GeneralSettings from "./pages/GeneralSettings";
import NotificationsPage from "./pages/NotificationsPage";
import MessagingPage from "./pages/MessagingPage";
import AdminDashboard from "./components/AdminDashboard";
import DashboardRouter from "./pages/dashboards/DashboardRouter";
import ManageTenants from "./pages/admin/ManageTenants";
import EmailTemplates from "./pages/admin/EmailTemplates";
import MessageQueue from "./pages/admin/MessageQueue";
import ManageWorkspaceOverview from "./pages/admin/ManageWorkspaceOverview";
import ManageFeatureApps from "./pages/admin/ManageFeatureApps";
import ProtectedRoute from "./components/ProtectedRoute";
import RoleProtectedRoute from "./components/RoleProtectedRoute";
import OEEFlowchartList from "./pages/flowchart/FlowchartList";
import OEEFlowchartBuilder from "./pages/flowchart/FlowchartBuilder";
import WebsiteLayout from "./website/components/layout/Layout";
import Home from "./website/pages/Home";
import MaxLayout from "./website-max/components/MaxLayout";
import MaxHome from "./website-max/pages/MaxHome";
import About from "./website/pages/About";
import PlannedMaintenanceLanding from "./website/pages/PlannedMaintenance";
import AuditManagement from "./website/pages/AuditManagement";
import OEELossAnalysis from "./website/pages/OEELossAnalysis";
import IncidentManagement from "./website/pages/IncidentManagement";
import RCACAPA from "./website/pages/RCACAPA";
import SafetyObservationLanding from "./website/pages/SafetyObservation";
import HazardReporting from "./website/pages/HazardReporting";
import BookNow from "./website/pages/BookNow";
import AIDashboard from "./pages/AIDashboard";
import "./styles/themes.css";
import "./App.css";
import MuiBrandThemeProvider from "./theme/MuiBrandThemeProvider";

/** Routes that need nav/module scope must stay inside this layout. Auth pages stay outside so login remount cannot tear down Router/Login mid-submit. */
function AuthenticatedAppShell() {
  return (
    <NavigationAccessBoundary>
      <Outlet />
    </NavigationAccessBoundary>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <MuiBrandThemeProvider>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        closeButton={true}
        style={{ zIndex: 10050 }}
      />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/purchase" element={<SubscriptionPurchase />} />
          <Route path="/" element={<MaxLayout />}>
            <Route index element={<MaxHome />} />
          </Route>
          <Route path="/legacy" element={<WebsiteLayout />}>
            <Route index element={<Home />} />
            <Route path="about" element={<About />} />
            <Route
              path="planned-maintenance"
              element={<PlannedMaintenanceLanding />}
            />
            <Route path="audit-management" element={<AuditManagement />} />
            <Route path="oee-loss-analysis" element={<OEELossAnalysis />} />
            <Route
              path="incident-management"
              element={<IncidentManagement />}
            />
            <Route path="rca-capa" element={<RoleProtectedRoute requiredPermission="RCA.View"><RCACAPA /></RoleProtectedRoute>} />
            <Route
              path="safety-observation"
              element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><SafetyObservationLanding /></RoleProtectedRoute>}
            />
            <Route path="hazard-reporting" element={<HazardReporting />} />
            <Route path="book-now" element={<BookNow />} />
          </Route>
          <Route path="/about" element={<Navigate to="/legacy/about" replace />} />
          <Route
            path="/planned-maintenance"
            element={<Navigate to="/legacy/planned-maintenance" replace />}
          />
          <Route
            path="/audit-management"
            element={<Navigate to="/legacy/audit-management" replace />}
          />
          <Route
            path="/oee-loss-analysis"
            element={<Navigate to="/legacy/oee-loss-analysis" replace />}
          />
          <Route
            path="/incident-management"
            element={<Navigate to="/legacy/incident-management" replace />}
          />
          <Route
            path="/rca-capa"
            element={<Navigate to="/legacy/rca-capa" replace />}
          />
          <Route
            path="/safety-observation"
            element={<Navigate to="/legacy/safety-observation" replace />}
          />
          <Route
            path="/hazard-reporting"
            element={<Navigate to="/legacy/hazard-reporting" replace />}
          />
          <Route path="/book-now" element={<Navigate to="/legacy/book-now" replace />} />

          <Route element={<AuthenticatedAppShell />}>
          {/* <Route
            path="/home"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route index element={<DashboardHome />} />
          <Route path="bi-dashboard" element={<BiDashboard />} />
          <Route path="metric-library" element={<MetricCardLibrary />} />
          <Route path="plants" element={<Plants />} />
          <Route path="machines" element={<Machines />} /> */}
          {/* <Route path="oee" element={<OEEAnalytics />} /> */}
          {/* <Route path="oee/reports" element={<OEEReportsDashboard />} /> */}
          {/* <Route path="oee-logs" element={<OEELogs />} /> */}
          {/* <Route path="oee-entry" element={<OeeDashboard />} />
            <Route path="oee-entry/:id" element={<OeeDashboard />} />
            <Route path="oee-dashboard" element={<OeeDashboard />} /> */}

          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardHome />} />
            <Route path="apps" element={<AppsHub />} />
            <Route path="apps/detail/:appCode" element={<AppDetailPage />} />
            <Route path="dashboard" element={<DashboardRouter />} />
            <Route path="ai-dashboard" element={<AIDashboard />} />
            <Route path="bi-dashboard" element={<BiDashboard />} />
            <Route path="metric-library" element={<MetricCardLibrary />} />
            <Route path="activity-log" element={<ActivityLog />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="messaging" element={<MessagingPage />} />
            <Route path="messaging/:conversationId" element={<MessagingPage />} />
            <Route path="settings/general" element={<GeneralSettings />} />

            <Route path="flowchart" element={<RoleProtectedRoute requiredPermission="OEE.View"><OEEFlowchartList /></RoleProtectedRoute>} />
            <Route path="flowchart/new" element={<RoleProtectedRoute requiredPermission="OEE.View"><OEEFlowchartBuilder /></RoleProtectedRoute>} />
            <Route
              path="flowchart/view/:id"
              element={<RoleProtectedRoute requiredPermission="OEE.View"><OEEFlowchartBuilder /></RoleProtectedRoute>}
            />
            <Route path="flowchart/:id" element={<RoleProtectedRoute requiredPermission="OEE.View"><OEEFlowchartBuilder /></RoleProtectedRoute>} />
            <Route path="oee" element={<RoleProtectedRoute requiredPermission="OEE.View"><OeeContainer /></RoleProtectedRoute>} />
            <Route path="oee/settings" element={<RoleProtectedRoute requiredPermission="OEE.View"><OeeSettings /></RoleProtectedRoute>} />
            <Route path="reports/oee-summary" element={<RoleProtectedRoute requiredPermission="OEE.View"><OeeSummaryReport /></RoleProtectedRoute>} />
            <Route path="reports/oee-loss" element={<RoleProtectedRoute requiredPermission="OEE.View"><OeeLossReport /></RoleProtectedRoute>} />
            <Route
              path="reports/oee-loss-trends"
              element={<RoleProtectedRoute requiredPermission="OEE.View"><OeeLossTrendsReport /></RoleProtectedRoute>}
            />
            <Route
              path="reports/oee-financial-loss"
              element={<RoleProtectedRoute requiredPermission="OEE.View"><OeeFinancialLossReport /></RoleProtectedRoute>}
            />

            <Route path="kaizen" element={<RoleProtectedRoute requiredPermission="Kaizen.View"><Kaizen /></RoleProtectedRoute>} />
            <Route path="kaizen-entry" element={<RoleProtectedRoute requiredPermission="Kaizen.View"><KaizenEntry /></RoleProtectedRoute>} />
            <Route path="kaizen-entry/:id" element={<RoleProtectedRoute requiredPermission="Kaizen.View"><KaizenEntry /></RoleProtectedRoute>} />
            <Route path="kaizen-actions" element={<RoleProtectedRoute requiredPermission="Kaizen.View"><KaizenActions /></RoleProtectedRoute>} />
            <Route path="fives" element={<FiveS />} />
            <Route path="fives-entry" element={<FiveSEntry />} />
            <Route path="fives-entry/:id" element={<FiveSEntry />} />
            <Route path="rca" element={<RoleProtectedRoute requiredPermission="RCA.View"><RCA /></RoleProtectedRoute>} />
            <Route path="rca-entry" element={<RoleProtectedRoute requiredPermission="RCA.View"><RCAEntry /></RoleProtectedRoute>} />
            <Route path="rca-entry/:id" element={<RoleProtectedRoute requiredPermission="RCA.View"><RCAEntry /></RoleProtectedRoute>} />
            <Route path="rca-actions" element={<RoleProtectedRoute requiredPermission="RCA.View"><RCAActions /></RoleProtectedRoute>} />
            <Route path="rca/fishbone" element={<RoleProtectedRoute requiredPermission="RCA.View"><FishboneAnalysis /></RoleProtectedRoute>} />
            <Route path="rca/five-whys" element={<RoleProtectedRoute requiredPermission="RCA.View"><FiveWhysAnalysis /></RoleProtectedRoute>} />
            <Route path="capa" element={<RoleProtectedRoute requiredPermission="CAPA.View"><CAPA /></RoleProtectedRoute>} />
            <Route path="capa-entry" element={<RoleProtectedRoute requiredPermission="CAPA.View"><CAPAEntry /></RoleProtectedRoute>} />
            <Route path="capa-entry/:id" element={<RoleProtectedRoute requiredPermission="CAPA.View"><CAPAEntry /></RoleProtectedRoute>} />
            <Route
              path="capa/task-management"
              element={<RoleProtectedRoute requiredPermission="CAPA.View"><CAPATaskManagement /></RoleProtectedRoute>}
            />
            <Route path="opl" element={<OPLDocuments />} />
            <Route path="users" element={<Users />} />
            <Route path="asset-master" element={<AssetMaster />} />
            <Route path="plants" element={<Plants />} />
            <Route path="subscription" element={<Subscription />} />
            <Route path="profile" element={<UserProfile />} />
            <Route path="reports" element={<RoleProtectedRoute requiredPermission="Reports.View"><ReportsDashboard /></RoleProtectedRoute>} />

            <Route path="reports/downtime" element={<RoleProtectedRoute requiredPermission="Reports.View"><DowntimeReport /></RoleProtectedRoute>} />
            <Route path="pm" element={<RoleProtectedRoute requiredPermission="MachineHealth.View"><PlannedMaintenanceDashboard /></RoleProtectedRoute>} />
            <Route
              path="pm/operator-tasks"
              element={
                <RoleProtectedRoute
                  requiredPermission="MachineHealth.View"
                >
                  <OperatorPmTasks />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="pm/calendar"
              element={
                <RoleProtectedRoute requiredPermission="PlannedMaintenance.View">
                  <PmCalendar />
                </RoleProtectedRoute>
              }
            />
            <Route path="reports/mtbr" element={<RoleProtectedRoute requiredPermission="Reports.View"><MTBRReport /></RoleProtectedRoute>} />
            <Route path="reports/mtbf" element={<RoleProtectedRoute requiredPermission="Reports.View"><MTBFReport /></RoleProtectedRoute>} />
            <Route
              path="reports/pm-schedule-compliance"
              element={<RoleProtectedRoute requiredPermission="Reports.View"><PmScheduleComplianceReport /></RoleProtectedRoute>}
            />
            <Route path="reports/pm-overdue" element={<RoleProtectedRoute requiredPermission="Reports.View"><PmOverdueReport /></RoleProtectedRoute>} />
            <Route path="reports/individual-tasks" element={<RoleProtectedRoute requiredPermission="Reports.View"><IndividualPmTaskReport /></RoleProtectedRoute>} />
            <Route path="reports/pm-standards-tracker" element={<RoleProtectedRoute requiredPermission="Reports.View"><PmStandardsTracker /></RoleProtectedRoute>} />
            <Route path="reports/hazard-reports" element={<RoleProtectedRoute requiredPermission="Reports.View"><HazardReports /></RoleProtectedRoute>} />
            <Route path="breakdown-log" element={<RoleProtectedRoute requiredPermission="Reports.View"><BreakdownLog /></RoleProtectedRoute>} />
            <Route path="loss-reasons" element={<LossReasons />} />
            <Route path="work-order" element={<WorkOrder />} />
            <Route path="work-order-wizard" element={<WorkOrderWizard />} />
            <Route path="work-order-wizard/:id" element={<WorkOrderWizard />} />
            <Route path="work-order-entry" element={<WorkOrder />} />
            <Route path="work-permits" element={<RoleProtectedRoute requiredPermission="WorkPermit.View"><WorkPermitList /></RoleProtectedRoute>} />
            <Route
              path="work-permits/dashboard"
              element={<RoleProtectedRoute requiredPermission="WorkPermit.View"><WorkPermitDashboard /></RoleProtectedRoute>}
            />
            <Route
              path="work-permits/reports/expiring"
              element={<RoleProtectedRoute requiredPermission="WorkPermit.View"><WorkPermitExpiringReport /></RoleProtectedRoute>}
            />
            <Route
              path="work-permits/reports/status"
              element={<RoleProtectedRoute requiredPermission="WorkPermit.View"><WorkPermitStatusReport /></RoleProtectedRoute>}
            />
            <Route
              path="work-permits/reports/compliance"
              element={<RoleProtectedRoute requiredPermission="WorkPermit.View"><WorkPermitComplianceReport /></RoleProtectedRoute>}
            />
            <Route path="work-permits/new" element={<RoleProtectedRoute requiredPermission="WorkPermit.View"><WorkPermitWizard /></RoleProtectedRoute>} />
            <Route path="work-permits/:id" element={<RoleProtectedRoute requiredPermission="WorkPermit.View"><WorkPermitWizard /></RoleProtectedRoute>} />
            <Route path="spare-parts" element={<SpareParts />} />
            <Route
              path="inventory/dashboard"
              element={<InventoryDashboard />}
            />
            <Route path="inventory/history" element={<InventoryHistory />} />
            <Route path="inventory/reports" element={<SparePartsReports />} />
            <Route path="safety-observation" element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><SafetyObservation /></RoleProtectedRoute>} />
            <Route
              path="safety-observation/dashboard"
              element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><SafetyObservationDashboard /></RoleProtectedRoute>}
            />
            <Route
              path="safety-observation/reports"
              element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><SafetyObservationReports /></RoleProtectedRoute>}
            />
            <Route
              path="safety-observation-entry"
              element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><SafetyObservationEntry /></RoleProtectedRoute>}
            />
            <Route
              path="safety-observation-entry/:id"
              element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><SafetyObservationEntry /></RoleProtectedRoute>}
            />
            <Route
              path="safety-observation-actions"
              element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><SafetyObservationActions /></RoleProtectedRoute>}
            />
            <Route path="safety-observation/schedule" element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><Schedule /></RoleProtectedRoute>} />
            <Route
              path="safety-observation/observation-type"
              element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><ObservationType /></RoleProtectedRoute>}
            />
            <Route
              path="safety-observation/settings"
              element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><SBOSettings /></RoleProtectedRoute>}
            />
            <Route
              path="safety-observation/site-layout"
              element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><SiteLayout /></RoleProtectedRoute>}
            />
            <Route path="incident/dashboard" element={<RoleProtectedRoute requiredPermission="Incident.View"><IncidentDashboard /></RoleProtectedRoute>} />
            <Route path="incident" element={<RoleProtectedRoute requiredPermission="Incident.View"><IncidentDashboard /></RoleProtectedRoute>} />
            <Route path="incident/new" element={<RoleProtectedRoute requiredPermission="Incident.View"><IncidentStepper /></RoleProtectedRoute>} />
            <Route path="incident/:id" element={<RoleProtectedRoute requiredPermission="Incident.View"><IncidentStepper /></RoleProtectedRoute>} />
            <Route path="settings/company" element={<Company />} />
            <Route path="settings/departments" element={<Departments />} />
            <Route path="settings/areas" element={<Areas />} />
            <Route path="settings/lines" element={<Lines />} />
            <Route path="settings/products" element={<Products />} />
            <Route path="settings/product-skus" element={<ProductSkus />} />
            <Route path="settings/shifts" element={<Shifts />} />
            <Route path="settings/roles" element={<Roles />} />
            <Route path="settings/permissions" element={<PermissionsPage />} />
            <Route path="settings/loss-types" element={<LossTypes />} />
            <Route path="settings/loss-subtypes" element={<LossSubTypes />} />
            <Route path="settings/categories" element={<Categories />} />
            <Route path="settings/statuses" element={<Statuses />} />
            <Route path="settings/triggers" element={<Triggers />} />
            <Route path="settings/cycle-times" element={<CycleTimes />} />
            <Route
              path="settings/planned-downtimes"
              element={<PlannedDowntimes />}
            />

            <Route
              path="settings/attachment-types"
              element={<AttachmentTypes />}
            />
            <Route path="settings/impact-levels" element={<ImpactLevels />} />

            <Route
              path="settings/root-cause-categories"
              element={<RootCauseCategories />}
            />
            <Route path="settings/failure-modes" element={<FailureModes />} />
            <Route path="settings/ppe-list" element={<PPEList />} />
            <Route
              path="settings/safety-behavior-categories"
              element={<SafetyBehaviorCategories />}
            />
            <Route
              path="settings/hazard-categories"
              element={<RoleProtectedRoute requiredPermission="Hazard.View"><HazardCategories /></RoleProtectedRoute>}
            />
            <Route
              path="settings/planned-maintenance-dashboard"
              element={
                <RoleProtectedRoute requiredPermission="Admin.Manage">
                  <PlannedMaintenanceDashboard />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="settings/pm-matrix"
              element={
                <RoleProtectedRoute requiredPermission="Admin.Manage">
                  <PmMatrixPlannerPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="settings/pm-masters"
              element={<PlannedMaintenanceMasters />}
            />
            <Route
              path="settings/pm-masters/maintenancePlans"
              element={<RoleProtectedRoute requiredPermission="PlannedMaintenance.View"><MaintenancePlansPage /></RoleProtectedRoute>}
            />
            <Route
              path="settings/pm-masters/maintenancePlanTasks"
              element={<MaintenancePlanTasksPage />}
            />
            <Route
              path="settings/pm-masters/pmSchedules"
              element={<RoleProtectedRoute requiredPermission="PlannedMaintenance.View"><PmSchedulesPage /></RoleProtectedRoute>}
            />
            <Route
              path="settings/pm-masters/maintenanceWindows"
              element={<MaintenanceWindowsPage />}
            />
            <Route
              path="settings/pm-masters/technicianSkills"
              element={<TechnicianSkillsPage />}
            />
            <Route
              path="settings/pm-masters/technicianTeams"
              element={<TechnicianTeamsPage />}
            />
            <Route path="settings/pm-masters/tools" element={<ToolsPage />} />
            <Route
              path="settings/pm-masters/ppeSafety"
              element={<PpeSafetyPage />}
            />
            <Route
              path="settings/pm-masters/sensorTags"
              element={<SensorTagsPage />}
            />
            <Route
              path="settings/pm-masters/sensorThresholds"
              element={<SensorThresholdsPage />}
            />
            <Route
              path="settings/pm-masters/pmTriggerLogs"
              element={<PmTriggerLogsPage />}
            />
            <Route
              path="settings/pm-masters/meterReadings"
              element={<MeterReadingsPage />}
            />
            <Route
              path="settings/pm-masters/pmHistory"
              element={<PmHistoryPage />}
            />
            <Route
              path="settings/pm-masters/pmCompliance"
              element={<PmCompliancePage />}
            />
            <Route
              path="settings/pm-masters/pmStandards"
              element={<PMStandardsPage />}
            />

            <Route
              path="settings/pm-masters/workOrderPriorities"
              element={<WorkOrderPrioritiesPage />}
            />
            <Route
              path="settings/pm-masters/lockoutTagout"
              element={<LockoutTagoutPage />}
            />
            <Route
              path="settings/pm-masters/sopDocuments"
              element={<SopDocumentsPage />}
            />
            <Route
              path="settings/pm-masters/holidayCalendar"
              element={<HolidayCalendarPage />}
            />
            <Route
              path="settings/pm-masters/vendors"
              element={<VendorsPage />}
            />
            <Route
              path="settings/pm-masters/maintenanceSla"
              element={<MaintenanceSlaPage />}
            />
            <Route
              path="settings/pm-masters/notificationTemplates"
              element={<NotificationTemplatesPage />}
            />
            <Route
              path="settings/contractor-masters"
              element={<ContractorMasters />}
            />
            <Route
              path="settings/change-password"
              element={<ChangePassword />}
            />
            <Route
              path="settings/master-data-import"
              element={<MasterDataImport />}
            />
            <Route path="settings/general" element={<GeneralSettings />} />
            <Route
              path="settings"
              element={<div>Settings — choose a category from the list or use Search (⌘K).</div>}
            />
            <Route
              path="flowchart/view/:id"
              element={<RoleProtectedRoute requiredPermission="OEE.View"><OEEFlowchartBuilder /></RoleProtectedRoute>}
            />
            <Route path="flowchart/:id" element={<RoleProtectedRoute requiredPermission="OEE.View"><OEEFlowchartBuilder /></RoleProtectedRoute>} />

            <Route path="kaizen" element={<RoleProtectedRoute requiredPermission="Kaizen.View"><Kaizen /></RoleProtectedRoute>} />
            <Route path="kaizen-entry" element={<RoleProtectedRoute requiredPermission="Kaizen.View"><KaizenEntry /></RoleProtectedRoute>} />
            <Route path="kaizen-entry/:id" element={<RoleProtectedRoute requiredPermission="Kaizen.View"><KaizenEntry /></RoleProtectedRoute>} />
            <Route path="kaizen-actions" element={<RoleProtectedRoute requiredPermission="Kaizen.View"><KaizenActions /></RoleProtectedRoute>} />
            <Route path="fives" element={<FiveS />} />
            <Route path="fives-entry" element={<FiveSEntry />} />
            <Route path="fives-entry/:id" element={<FiveSEntry />} />
            <Route path="rca" element={<RoleProtectedRoute requiredPermission="RCA.View"><RCA /></RoleProtectedRoute>} />
            <Route path="rca-entry" element={<RoleProtectedRoute requiredPermission="RCA.View"><RCAEntry /></RoleProtectedRoute>} />
            <Route path="rca-entry/:id" element={<RoleProtectedRoute requiredPermission="RCA.View"><RCAEntry /></RoleProtectedRoute>} />
            <Route path="rca-actions" element={<RoleProtectedRoute requiredPermission="RCA.View"><RCAActions /></RoleProtectedRoute>} />
            <Route path="rca/fishbone" element={<RoleProtectedRoute requiredPermission="RCA.View"><FishboneAnalysis /></RoleProtectedRoute>} />
            <Route path="rca/five-whys" element={<RoleProtectedRoute requiredPermission="RCA.View"><FiveWhysAnalysis /></RoleProtectedRoute>} />
            <Route path="capa" element={<RoleProtectedRoute requiredPermission="CAPA.View"><CAPA /></RoleProtectedRoute>} />
            <Route path="capa-entry" element={<RoleProtectedRoute requiredPermission="CAPA.View"><CAPAEntry /></RoleProtectedRoute>} />
            <Route path="capa-entry/:id" element={<RoleProtectedRoute requiredPermission="CAPA.View"><CAPAEntry /></RoleProtectedRoute>} />
            <Route
              path="capa/task-management"
              element={<RoleProtectedRoute requiredPermission="CAPA.View"><CAPATaskManagement /></RoleProtectedRoute>}
            />
            <Route path="opl" element={<OPLDocuments />} />
            <Route path="users" element={<Users />} />
            <Route path="subscription" element={<Subscription />} />
            <Route path="reports" element={<RoleProtectedRoute requiredPermission="Reports.View"><ReportsDashboard /></RoleProtectedRoute>} />
            {/* <Route path="reports/oee-summary" element={<OEESummaryReport />} /> */}
            {/* <Route
              path="reports/shift-summary"
              element={<ShiftSummaryReport />}
            /> */}
            {/* <Route
              path="reports/loss-summary"
              element={<LossSummaryReport />}
            />
            <Route
              path="reports/sku-performance"
              element={<SKUPerformanceReport />}
            /> */}
            <Route path="reports/downtime" element={<RoleProtectedRoute requiredPermission="Reports.View"><DowntimeReport /></RoleProtectedRoute>} />
            <Route path="pm" element={<RoleProtectedRoute requiredPermission="PlannedMaintenance.View"><MaintenancePlansPage /></RoleProtectedRoute>} />
            <Route path="reports/mtbr" element={<RoleProtectedRoute requiredPermission="Reports.View"><MTBRReport /></RoleProtectedRoute>} />
            <Route path="reports/mtbf" element={<RoleProtectedRoute requiredPermission="Reports.View"><MTBFReport /></RoleProtectedRoute>} />
            <Route
              path="reports/pm-schedule-compliance"
              element={<RoleProtectedRoute requiredPermission="Reports.View"><PmScheduleComplianceReport /></RoleProtectedRoute>}
            />
            <Route path="reports/pm-overdue" element={<RoleProtectedRoute requiredPermission="Reports.View"><PmOverdueReport /></RoleProtectedRoute>} />
            <Route path="reports/individual-tasks" element={<RoleProtectedRoute requiredPermission="Reports.View"><IndividualPmTaskReport /></RoleProtectedRoute>} />
            <Route path="breakdown-log" element={<RoleProtectedRoute requiredPermission="Reports.View"><BreakdownLog /></RoleProtectedRoute>} />
            <Route path="loss-reasons" element={<LossReasons />} />
            <Route path="work-order" element={<WorkOrder />} />
            <Route path="work-order-wizard" element={<WorkOrderWizard />} />
            <Route path="work-order-wizard/:id" element={<WorkOrderWizard />} />
            <Route path="work-order-entry" element={<WorkOrder />} />
            <Route path="work-permits" element={<RoleProtectedRoute requiredPermission="WorkPermit.View"><WorkPermitList /></RoleProtectedRoute>} />
            <Route
              path="work-permits/dashboard"
              element={<RoleProtectedRoute requiredPermission="WorkPermit.View"><WorkPermitDashboard /></RoleProtectedRoute>}
            />
            <Route
              path="work-permits/reports/expiring"
              element={<RoleProtectedRoute requiredPermission="WorkPermit.View"><WorkPermitExpiringReport /></RoleProtectedRoute>}
            />
            <Route
              path="work-permits/reports/status"
              element={<RoleProtectedRoute requiredPermission="WorkPermit.View"><WorkPermitStatusReport /></RoleProtectedRoute>}
            />
            <Route
              path="work-permits/reports/compliance"
              element={<RoleProtectedRoute requiredPermission="WorkPermit.View"><WorkPermitComplianceReport /></RoleProtectedRoute>}
            />
            <Route path="work-permits/new" element={<RoleProtectedRoute requiredPermission="WorkPermit.View"><WorkPermitWizard /></RoleProtectedRoute>} />
            <Route path="work-permits/:id" element={<RoleProtectedRoute requiredPermission="WorkPermit.View"><WorkPermitWizard /></RoleProtectedRoute>} />
            <Route path="spare-parts" element={<SpareParts />} />
            <Route
              path="inventory/dashboard"
              element={<InventoryDashboard />}
            />
            <Route path="inventory/history" element={<InventoryHistory />} />
            <Route path="inventory/reports" element={<SparePartsReports />} />
            <Route path="inventory/reports/:reportType" element={<SparePartsReports />} />
            <Route path="safety-observation" element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><SafetyObservation /></RoleProtectedRoute>} />
            <Route
              path="safety-observation/dashboard"
              element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><SafetyObservationDashboard /></RoleProtectedRoute>}
            />
            <Route
              path="safety-observation/reports"
              element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><SafetyObservationReports /></RoleProtectedRoute>}
            />
            <Route
              path="safety-observation-entry"
              element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><SafetyObservationEntry /></RoleProtectedRoute>}
            />
            <Route
              path="safety-observation-entry/:id"
              element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><SafetyObservationEntry /></RoleProtectedRoute>}
            />
            <Route
              path="safety-observation-actions"
              element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><SafetyObservationActions /></RoleProtectedRoute>}
            />
            <Route path="safety-observation/schedule" element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><Schedule /></RoleProtectedRoute>} />
            <Route
              path="safety-observation/observation-type"
              element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><ObservationType /></RoleProtectedRoute>}
            />
            <Route
              path="safety-observation/settings"
              element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><SBOSettings /></RoleProtectedRoute>}
            />
            <Route path="settings/site-layout" element={<RoleProtectedRoute requiredPermission="SafetyObservation.View"><SiteLayout /></RoleProtectedRoute>} />
            <Route
              path="incident/analytics"
              element={<RoleProtectedRoute requiredPermission="Incident.View"><IncidentAnalyticsDashboard /></RoleProtectedRoute>}
            />
            <Route path="incident/dashboard" element={<RoleProtectedRoute requiredPermission="Incident.View"><IncidentDashboard /></RoleProtectedRoute>} />
            <Route path="incident" element={<RoleProtectedRoute requiredPermission="Incident.View"><IncidentDashboard /></RoleProtectedRoute>} />
            <Route path="incident/new" element={<RoleProtectedRoute requiredPermission="Incident.View"><IncidentStepper /></RoleProtectedRoute>} />
            <Route path="incident/:id" element={<RoleProtectedRoute requiredPermission="Incident.View"><IncidentStepper /></RoleProtectedRoute>} />
            <Route path="hazard-reports" element={<RoleProtectedRoute requiredPermission="Hazard.View"><HazardReportList /></RoleProtectedRoute>} />
            <Route
              path="hazard-reports/analytics"
              element={<RoleProtectedRoute requiredPermission="Hazard.View"><HazardReportDashboard /></RoleProtectedRoute>}
            />
            <Route path="hazard-reports/new" element={<RoleProtectedRoute requiredPermission="Hazard.View"><HazardReportForm /></RoleProtectedRoute>} />
            <Route
              path="hazard-reports/edit/:id"
              element={<RoleProtectedRoute requiredPermission="Hazard.View"><HazardReportForm /></RoleProtectedRoute>}
            />
            <Route
              path="hazard-reports/settings"
              element={<RoleProtectedRoute requiredPermission="Hazard.View"><HazardSettings /></RoleProtectedRoute>}
            />
            <Route
              path="Hazard-Reporting/hazard-categories"
              element={<RoleProtectedRoute requiredPermission="Hazard.View"><HazardCategories /></RoleProtectedRoute>}
            />

            <Route path="settings/company" element={<Company />} />
            <Route path="settings/departments" element={<Departments />} />
            <Route path="settings/areas" element={<Areas />} />
            <Route path="settings/lines" element={<Lines />} />
            <Route path="settings/products" element={<Products />} />
            <Route path="settings/product-skus" element={<ProductSkus />} />
            <Route path="settings/shifts" element={<Shifts />} />
            <Route path="settings/roles" element={<Roles />} />
            <Route path="settings/permissions" element={<PermissionsPage />} />
            <Route path="settings/loss-types" element={<LossTypes />} />
            <Route path="settings/loss-subtypes" element={<LossSubTypes />} />
            <Route path="settings/categories" element={<Categories />} />
            <Route path="settings/statuses" element={<Statuses />} />
            <Route path="settings/triggers" element={<Triggers />} />
            <Route path="settings/cycle-times" element={<CycleTimes />} />
            <Route
              path="settings/planned-downtimes"
              element={<PlannedDowntimes />}
            />
            <Route path="settings/oee-thresholds" element={<OEEThresholds />} />
            <Route
              path="settings/attachment-types"
              element={<AttachmentTypes />}
            />
            <Route path="settings/impact-levels" element={<ImpactLevels />} />

            <Route
              path="settings/root-cause-categories"
              element={<RootCauseCategories />}
            />
            <Route path="settings/failure-modes" element={<FailureModes />} />
            <Route path="settings/ppe-list" element={<PPEList />} />
            <Route
              path="settings/safety-behavior-categories"
              element={<SafetyBehaviorCategories />}
            />
            <Route
              path="settings/planned-maintenance-dashboard"
              element={<PlannedMaintenanceDashboard />}
            />
            <Route
              path="settings/pm-matrix"
              element={<PmMatrixPlannerPage />}
            />
            <Route
              path="settings/pm-masters"
              element={<PlannedMaintenanceMasters />}
            />
            <Route
              path="settings/pm-masters/maintenancePlans"
              element={<RoleProtectedRoute requiredPermission="PlannedMaintenance.View"><MaintenancePlansPage /></RoleProtectedRoute>}
            />
            <Route
              path="settings/pm-masters/maintenancePlanTasks"
              element={<MaintenancePlanTasksPage />}
            />
            <Route
              path="settings/pm-masters/pmSchedules"
              element={<RoleProtectedRoute requiredPermission="PlannedMaintenance.View"><PmSchedulesPage /></RoleProtectedRoute>}
            />
            <Route
              path="settings/pm-masters/maintenanceWindows"
              element={<MaintenanceWindowsPage />}
            />
            <Route
              path="settings/pm-masters/technicianSkills"
              element={<TechnicianSkillsPage />}
            />
            <Route
              path="settings/pm-masters/technicianTeams"
              element={<TechnicianTeamsPage />}
            />
            <Route path="settings/pm-masters/tools" element={<ToolsPage />} />
            <Route
              path="settings/pm-masters/ppeSafety"
              element={<PpeSafetyPage />}
            />
            <Route
              path="settings/pm-masters/sensorTags"
              element={<SensorTagsPage />}
            />
            <Route
              path="settings/pm-masters/sensorThresholds"
              element={<SensorThresholdsPage />}
            />
            <Route
              path="settings/pm-masters/pmTriggerLogs"
              element={<PmTriggerLogsPage />}
            />
            <Route
              path="settings/pm-masters/meterReadings"
              element={<MeterReadingsPage />}
            />
            <Route
              path="settings/pm-masters/pmHistory"
              element={<PmHistoryPage />}
            />
            <Route
              path="settings/pm-masters/pmCompliance"
              element={<PmCompliancePage />}
            />
            <Route
              path="settings/pm-masters/pmStandards"
              element={
                <RoleProtectedRoute requiredPermission="Admin.Manage">
                  <PMStandardsPage />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="settings/pm-masters/workOrderPriorities"
              element={<WorkOrderPrioritiesPage />}
            />
            <Route
              path="settings/pm-masters/lockoutTagout"
              element={<LockoutTagoutPage />}
            />
            <Route
              path="settings/pm-masters/sopDocuments"
              element={<SopDocumentsPage />}
            />
            <Route
              path="settings/pm-masters/holidayCalendar"
              element={<HolidayCalendarPage />}
            />
            <Route
              path="settings/pm-masters/vendors"
              element={<VendorsPage />}
            />
            <Route
              path="settings/pm-masters/maintenanceSla"
              element={<MaintenanceSlaPage />}
            />
            <Route
              path="settings/pm-masters/notificationTemplates"
              element={<NotificationTemplatesPage />}
            />
            <Route
              path="settings/contractor-masters"
              element={<ContractorMasters />}
            />
            <Route
              path="settings/change-password"
              element={<ChangePassword />}
            />
            <Route
              path="settings/master-data-import"
              element={<MasterDataImport />}
            />
            <Route
              path="settings"
              element={<div>Settings — choose a category from the list or use Search (⌘K).</div>}
            />
          </Route>
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="tenants" replace />} />
            <Route path="tenants" element={<RoleProtectedRoute requiredPermission="Admin.Manage"><ManageTenants /></RoleProtectedRoute>} />
            <Route
              path="workspace-overview"
              element={<RoleProtectedRoute requiredPermission="Admin.Manage"><ManageWorkspaceOverview /></RoleProtectedRoute>}
            />
            <Route
              path="feature-apps"
              element={<RoleProtectedRoute requiredPermission="Admin.Manage"><ManageFeatureApps /></RoleProtectedRoute>}
            />
            <Route path="email-templates" element={<RoleProtectedRoute requiredPermission="Admin.Manage"><EmailTemplates /></RoleProtectedRoute>} />
            <Route path="message-queue" element={<RoleProtectedRoute requiredPermission="Admin.Manage"><MessageQueue /></RoleProtectedRoute>} />
            <Route path="activity-log" element={<RoleProtectedRoute requiredPermission="Admin.Manage"><ActivityLog /></RoleProtectedRoute>} />
          </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </MuiBrandThemeProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
